import type { ContinuityRecordKind, ContinuityRecordSummary } from "@/lib/behavior/continuityRecord";

const MILESTONE_COUNTS = new Set([1, 2, 5, 10, 15, 25]);

function normalizeLeaderName(value?: string | null): string | null {
  const normalized = value?.replace(/\s+/g, " ").trim();
  return normalized || null;
}

function recordUpdateLabel(kind: ContinuityRecordKind, count: number): string {
  const noun = kind === "leadership" ? "leadership record update" : "coaching record update";
  return count === 1 ? noun : `${noun}s`;
}

export function getContinuityMilestone(count: number): number | null {
  if (count < 1) return null;
  if (MILESTONE_COUNTS.has(count)) return count;
  if (count > 25 && count % 25 === 0) return count;
  return null;
}

function buildContinuityDescription(params: {
  kind: ContinuityRecordKind;
  relationshipLeaderName?: string | null;
}): string {
  const leaderName = normalizeLeaderName(params.relationshipLeaderName);
  if (params.kind === "leadership") {
    return "Your Leadership Record is building continuity over time.";
  }
  if (leaderName) {
    return `${leaderName}'s Coaching Record is building continuity over time.`;
  }
  return "Your Coaching Record is building continuity over time.";
}

export function shouldAnnounceContinuityMilestone(params: {
  count: number;
  lastAnnouncedMilestone?: number | null;
}): boolean {
  const milestone = getContinuityMilestone(params.count);
  if (!milestone) return false;
  return milestone > (params.lastAnnouncedMilestone ?? 0);
}

export function buildContinuityMomentToastCopy(params: {
  kind: ContinuityRecordKind;
  count: number;
  relationshipLeaderName?: string | null;
}): { title: string; description: string } | null {
  const milestone = getContinuityMilestone(params.count);
  if (!milestone) return null;

  const leaderName = normalizeLeaderName(params.relationshipLeaderName);
  return {
    title: `${params.count} ${recordUpdateLabel(params.kind, params.count)}${leaderName ? ` for ${leaderName}` : ""}`,
    description: buildContinuityDescription({
      kind: params.kind,
      relationshipLeaderName: leaderName,
    }),
  };
}

export function buildContinuityToastStorageKey(params: {
  userId: string;
  kind: ContinuityRecordKind;
  scopeId?: string | null;
}): string {
  return [
    "coach-veera-continuity-moment-toast",
    params.userId,
    params.kind,
    params.scopeId || "all",
  ].join(":");
}

export function readLastAnnouncedContinuityMilestone(storage: Storage, key: string): number {
  const raw = storage.getItem(key);
  const parsed = raw ? Number.parseInt(raw, 10) : 0;
  return Number.isFinite(parsed) ? parsed : 0;
}

export function writeLastAnnouncedContinuityMilestone(storage: Storage, key: string, count: number): void {
  storage.setItem(key, String(count));
}

export function resolveContinuityToastAnnouncement(params: {
  summary: ContinuityRecordSummary | null | undefined;
  lastAnnouncedMilestone?: number | null;
  relationshipLeaderName?: string | null;
}): { milestone: number; copy: { title: string; description: string } } | null {
  if (!params.summary) return null;
  const kind: ContinuityRecordKind = params.summary.label === "Leadership Record" ? "leadership" : "coaching";
  const milestone = getContinuityMilestone(params.summary.count);
  if (!milestone || !shouldAnnounceContinuityMilestone({
    count: params.summary.count,
    lastAnnouncedMilestone: params.lastAnnouncedMilestone,
  })) {
    return null;
  }
  const copy = buildContinuityMomentToastCopy({
    kind,
    count: params.summary.count,
    relationshipLeaderName: params.relationshipLeaderName,
  });
  return copy ? { milestone, copy } : null;
}
